// Device-held records policy: unsynced field work is never auto-deleted; cached snapshots expire; on sign-out the user chooses sync / keep / discard.
export const SNAPSHOT_TTL_MS = 24 * 3600e3;
export const STALE_AFTER_MS = 7 * 86400e3;
export const FIELD_KEY = "qs_field_queue";
export const TICKS_KEY = "qs_visit_ticks";
const PARK_PREFIX = "qs_parked_";
const parkKey = (userId) => `${PARK_PREFIX}${ownerKey(userId)}`;
const VISIT_PREFIX = "qs_visit_cache_";
const SNAPSHOT_KEYS = ["qs_quality_cache", "qs_device_id"];

const readJ = (k, d) => { try { return JSON.parse(localStorage.getItem(k) ?? "null") ?? d; } catch { return d; } };
const writeJ = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const ageMs = (iso) => (iso ? Date.now() - Date.parse(iso) : 0);
const visitKeys = () => Object.keys(localStorage).filter((k) => k.startsWith(VISIT_PREFIX));

// Non-reversible scope key so the parked bundle never stores the raw account id.
export const ownerKey = (userId) => { let h = 0x811c9dc5; for (const ch of String(userId)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; } return h.toString(16).padStart(8, "0"); };

export const isStale = (iso) => ageMs(iso) > STALE_AFTER_MS;
export const isExpired = (iso) => !iso || ageMs(iso) > SNAPSHOT_TTL_MS;

export const inventory = (userId) => {
  const field = readJ(FIELD_KEY, []), ticks = readJ(TICKS_KEY, []);
  const stale = [...field.map((f) => f.captured_at), ...ticks.map((t) => t.at)].filter(isStale).length;
  const parked = userId != null ? readJ(parkKey(userId), null) : null;
  return { field: field.length, ticks: ticks.length, unsynced: field.length + ticks.length, stale, snapshots: visitKeys().length, parked: parked ? parked.field.length + parked.ticks.length : 0 };
};

export const expireSnapshots = () => {
  let removed = 0;
  visitKeys().forEach((k) => { if (isExpired(readJ(k, null)?.cached_at)) { localStorage.removeItem(k); removed += 1; } });
  const q = readJ("qs_quality_cache", []);
  const kept = q.filter((x) => ageMs(x.cached_at) <= STALE_AFTER_MS);
  if (kept.length !== q.length) { writeJ("qs_quality_cache", kept); removed += q.length - kept.length; }
  return removed;
};

export const clearSyncedCaches = () => { visitKeys().forEach((k) => localStorage.removeItem(k)); SNAPSHOT_KEYS.forEach((k) => localStorage.removeItem(k)); };

// Each account gets its own parked bundle, so one user's "keep" can never overwrite another's.
export const parkUnsynced = (userId) => {
  const field = readJ(FIELD_KEY, []), ticks = readJ(TICKS_KEY, []);
  if (!field.length && !ticks.length) return 0;
  const prev = readJ(parkKey(userId), { field: [], ticks: [] });
  writeJ(parkKey(userId), { owner: ownerKey(userId), parked_at: new Date().toISOString(), field: [...prev.field, ...field], ticks: [...prev.ticks, ...ticks] });
  localStorage.removeItem(FIELD_KEY); localStorage.removeItem(TICKS_KEY);
  return field.length + ticks.length;
};

export const restoreParked = (userId) => {
  const p = readJ(parkKey(userId), null);
  if (!p) return 0;
  const field = readJ(FIELD_KEY, []), ticks = readJ(TICKS_KEY, []);
  const fieldIds = new Set(field.map((f) => f.client_id)), tickIds = new Set(ticks.map((t) => `${t.visit_id}|${t.followup_id}`));
  const addF = p.field.filter((f) => !fieldIds.has(f.client_id)), addT = p.ticks.filter((t) => !tickIds.has(`${t.visit_id}|${t.followup_id}`));
  writeJ(FIELD_KEY, [...field, ...addF]); writeJ(TICKS_KEY, [...ticks, ...addT]);
  localStorage.removeItem(parkKey(userId));
  return addF.length + addT.length;
};

export const discardUnsynced = () => { const n = inventory().unsynced; localStorage.removeItem(FIELD_KEY); localStorage.removeItem(TICKS_KEY); return n; };

export const POLICY = [
  "Unsynced captures and visit ticks are never auto-deleted",
  "Items older than 7 days are flagged stale so you can review them",
  "Cached visit snapshots expire after 24 h; passport caches after 7 days",
  "On sign-out you choose: sync now · keep on this device (hidden until you sign back in) · discard",
];
